/**
 * KnowledgeDetailModal.jsx - Document detail view
 *
 * Shows full document metadata, synopsis, topics, and chunk preview.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { FileText, Book, Tag, Calendar, Hash, ExternalLink, ChevronDown, ChevronUp, Shield, AlertTriangle, BarChart3, Microscope, Pencil } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import ModalWrapper, { ModalCard } from './ModalWrapper';
import { useTranslation } from '../hooks/useTranslation';

// ─── Inline edit primitive ──────────────────────────────────────────────────
// Click-to-reveal input/textarea, blur OR Enter (single-line) commits,
// Escape cancels. Used for the editable title/author/synopsis trio.

const EditTrigger = styled.button`
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  padding: 6px 10px;
  margin: -6px -10px;
  color: inherit;
  font: inherit;
  text-align: left;
  border-radius: 6px;
  cursor: text;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  &:hover { background: rgba(255, 255, 255, 0.04); border-color: rgba(255,255,255,0.3); }
  & > .pencil {
    flex-shrink: 0;
    opacity: 0.4;
    margin-top: 4px;
    transition: opacity 120ms;
  }
  &:hover > .pencil { opacity: 0.8; }
`;

const EditInput = styled.input`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(147, 51, 234, 0.5);
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font: inherit;
  width: 100%;
  outline: none;
  &:focus { border-color: rgba(147, 51, 234, 0.9); }
`;

const EditTextarea = styled.textarea`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(147, 51, 234, 0.5);
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font: inherit;
  width: 100%;
  min-height: 120px;
  resize: vertical;
  outline: none;
  &:focus { border-color: rgba(147, 51, 234, 0.9); }
`;

const PlaceholderText = styled.span`
  color: rgba(255, 255, 255, 0.35);
  font-style: italic;
`;

function EditableField({ value, placeholder, multiline = false, onSave, render, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = useCallback(async () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (value || '').trim()) return;
    try { await onSave(next); } catch { setDraft(value || ''); }
  }, [draft, value, onSave]);

  if (disabled) return render(value);

  if (!editing) {
    return (
      <EditTrigger onClick={() => { setDraft(value || ''); setEditing(true); }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          {value ? render(value) : <PlaceholderText>{placeholder}</PlaceholderText>}
        </span>
        <Pencil size={12} className="pencil" />
      </EditTrigger>
    );
  }

  const handleKey = (e) => {
    if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
  };

  return multiline ? (
    <EditTextarea
      autoFocus value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKey}
      placeholder={placeholder}
    />
  ) : (
    <EditInput
      autoFocus value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKey}
      placeholder={placeholder}
    />
  );
}

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;



const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 12px 0;
`;

const Synopsis = styled.div`
  font-size: 15px;
  color: #ccc;
  line-height: 1.7;
  padding: 16px;
  background: rgba(147, 51, 234, 0.1);
  border-radius: 12px;
  border-left: 3px solid #9333ea;
`;

const NoSynopsis = styled.div`
  font-size: 14px;
  color: #666;
  font-style: italic;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
`;

const Topics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TopicTag = styled.span`
  padding: 6px 14px;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const MetadataItem = styled.div`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

const MetadataLabel = styled.div`
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const MetadataValue = styled.div`
  font-size: 14px;
  color: #fff;
  font-weight: 500;
`;

const ChunksSection = styled.div`
  margin-top: 24px;
`;

const ChunksHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ChunksList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChunkItem = styled.div`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  font-size: 13px;
  color: #aaa;
  line-height: 1.6;

  strong {
    display: block;
    color: #888;
    font-size: 11px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
`;

const LoadingChunks = styled.div`
  padding: 24px;
  text-align: center;
  color: #666;
`;

// ─── Quality Analysis ─────────────────────────────────────────

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const ScoreCard = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  text-align: center;
`;

const ScoreLabel = styled.div`
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const ScoreValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => {
    const v = props.$value;
    if (v >= 0.7) return '#4ade80';
    if (v >= 0.4) return '#facc15';
    return '#f87171';
  }};
`;

const ScoreBar = styled.div`
  height: 3px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => (props.$value || 0) * 100}%;
    background: ${props => {
      const v = props.$value;
      if (v >= 0.7) return '#4ade80';
      if (v >= 0.4) return '#facc15';
      return '#f87171';
    }};
    border-radius: 2px;
  }
`;

const ReasoningBox = styled.div`
  font-size: 14px;
  color: #bbb;
  line-height: 1.7;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border-left: 3px solid ${props => props.$color || 'rgba(147, 51, 234, 0.5)'};
  margin-bottom: 12px;
`;

const ReasoningLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg { width: 13px; height: 13px; opacity: 0.6; }
`;

const QualityTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

const QualityTag = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$bg || 'rgba(255,255,255,0.05)'};
  color: ${props => props.$color || '#aaa'};
`;

const formatDate = (dateString, language = 'fi') => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const dateLocale = language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI';
  return date.toLocaleDateString(dateLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const KnowledgeDetailModal = ({ document, onClose, onUpdate }) => {
  const { language } = useTranslation();
  // Editable when parent passes onUpdate (admin context). Otherwise read-only.
  const editable = typeof onUpdate === 'function';
  const saveField = useCallback((field) => async (value) => {
    await onUpdate(document.id, { [field]: value });
  }, [document.id, onUpdate]);
  const [chunks, setChunks] = useState([]);
  const [enrichmentStats, setEnrichmentStats] = useState(null);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [showChunks, setShowChunks] = useState(false);
  const [showEnrichment, setShowEnrichment] = useState(false);

  const fetchChunks = async () => {
    if (chunks.length > 0) return;

    try {
      setLoadingChunks(true);
      const response = await fetch(API_ENDPOINTS.RAG_DOCUMENT(document.id));
      const data = await response.json();
      setChunks(data.chunks?.slice(0, 20) || []);
      if (data.enrichmentStats) setEnrichmentStats(data.enrichmentStats);
    } catch (err) {
      console.error('Error fetching chunks:', err);
    } finally {
      setLoadingChunks(false);
    }
  };

  // Auto-fetch enrichment stats on mount
  useEffect(() => {
    if (!enrichmentStats) fetchChunks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleChunks = () => {
    if (!showChunks) {
      fetchChunks();
    }
    setShowChunks(!showChunks);
  };

  return (
    <ModalWrapper visible onClose={onClose} zIndex={1000} blur={8} opacity={0.85}>
      <ModalCard size="lg" onClick={(e) => e.stopPropagation()} style={{ border: '1px solid rgba(147, 51, 234, 0.3)' }}>
        <ModalCard.Header>
          <HeaderContent>
            <Title>
              <EditableField
                value={document.title || ''}
                placeholder={document.filename || 'Otsikko'}
                onSave={saveField('title')}
                disabled={!editable}
                render={(v) => v || document.filename}
              />
            </Title>
            <Subtitle>
              <span>@ <EditableField
                value={document.author || ''}
                placeholder="Lisää julkaisija"
                onSave={saveField('author')}
                disabled={!editable}
                render={(v) => v}
              /></span>
              {document.category && (
                <span style={{
                  padding: '2px 8px',
                  background: 'rgba(147, 51, 234, 0.2)',
                  borderRadius: '6px',
                  color: '#c084fc',
                  fontSize: '12px'
                }}>
                  {document.category}
                </span>
              )}
            </Subtitle>
          </HeaderContent>
        </ModalCard.Header>

        <ModalCard.Body>
          <Section>
            <SectionTitle>Tiivistelmä</SectionTitle>
            <EditableField
              value={document.synopsis || ''}
              placeholder={editable
                ? 'Klikkaa kirjoittaaksesi tiivistelmän tai generoi se ajamalla synopsis-skripti.'
                : 'Tiivistelmää ei ole vielä luotu.'}
              multiline
              onSave={saveField('synopsis')}
              disabled={!editable}
              render={(v) => v
                ? <Synopsis as="div">{v}</Synopsis>
                : <NoSynopsis>Tiivistelmää ei ole vielä luotu.</NoSynopsis>}
            />
          </Section>

          {document.source_quality && Object.keys(document.source_quality).length > 0 && (() => {
            const sq = document.source_quality;
            return (
              <Section>
                <SectionTitle>Lähdeanalyysi</SectionTitle>

                <ScoreGrid>
                  {sq.credibility_score != null && (
                    <ScoreCard>
                      <ScoreLabel>Uskottavuus</ScoreLabel>
                      <ScoreValue $value={sq.credibility_score}>
                        {(sq.credibility_score * 100).toFixed(0)}%
                      </ScoreValue>
                      <ScoreBar $value={sq.credibility_score} />
                    </ScoreCard>
                  )}
                  {sq.relevance_score != null && (
                    <ScoreCard>
                      <ScoreLabel>Relevanssi</ScoreLabel>
                      <ScoreValue $value={sq.relevance_score}>
                        {(sq.relevance_score * 100).toFixed(0)}%
                      </ScoreValue>
                      <ScoreBar $value={sq.relevance_score} />
                    </ScoreCard>
                  )}
                  {sq.research_basis_score != null && (
                    <ScoreCard>
                      <ScoreLabel>Tutkimuspohja</ScoreLabel>
                      <ScoreValue $value={sq.research_basis_score}>
                        {(sq.research_basis_score * 100).toFixed(0)}%
                      </ScoreValue>
                      <ScoreBar $value={sq.research_basis_score} />
                    </ScoreCard>
                  )}
                  {sq.methodological_transparency != null && (
                    <ScoreCard>
                      <ScoreLabel>Menetelmät</ScoreLabel>
                      <ScoreValue $value={sq.methodological_transparency}>
                        {(sq.methodological_transparency * 100).toFixed(0)}%
                      </ScoreValue>
                      <ScoreBar $value={sq.methodological_transparency} />
                    </ScoreCard>
                  )}
                  {sq.recency_score != null && (
                    <ScoreCard>
                      <ScoreLabel>Tuoreus</ScoreLabel>
                      <ScoreValue $value={sq.recency_score}>
                        {(sq.recency_score * 100).toFixed(0)}%
                      </ScoreValue>
                      <ScoreBar $value={sq.recency_score} />
                    </ScoreCard>
                  )}
                </ScoreGrid>

                <QualityTagRow>
                  {sq.document_type && (
                    <QualityTag $bg="rgba(147, 51, 234, 0.15)" $color="#c084fc">
                      {sq.document_type}
                    </QualityTag>
                  )}
                  {sq.evidence_type && (
                    <QualityTag $bg="rgba(99, 102, 241, 0.15)" $color="#a5b4fc">
                      {sq.evidence_type}
                    </QualityTag>
                  )}
                  {sq.publisher_type && (
                    <QualityTag $bg="rgba(59, 130, 246, 0.12)" $color="#93c5fd">
                      {sq.publisher_type}
                    </QualityTag>
                  )}
                  {sq.stakeholder_perspective && (
                    <QualityTag>
                      {sq.stakeholder_perspective}
                    </QualityTag>
                  )}
                  {sq.publication_year && (
                    <QualityTag>
                      {sq.publication_year}
                    </QualityTag>
                  )}
                </QualityTagRow>

                {sq.credibility_reasoning && (
                  <ReasoningBox $color="rgba(34, 197, 94, 0.5)">
                    <ReasoningLabel><Shield /> Uskottavuusarvio</ReasoningLabel>
                    {sq.credibility_reasoning}
                  </ReasoningBox>
                )}

                {sq.bias_direction && sq.bias_direction !== 'neutral' && (
                  <ReasoningBox $color="rgba(251, 146, 60, 0.5)">
                    <ReasoningLabel><AlertTriangle /> Näkökulmavääristymä</ReasoningLabel>
                    {typeof sq.bias_direction === 'string' ? sq.bias_direction : ''}
                    {sq.bias_indicators && sq.bias_indicators.length > 0 && (
                      <QualityTagRow style={{ marginTop: 8, marginBottom: 0 }}>
                        {sq.bias_indicators.map((ind, i) => (
                          <QualityTag key={i} $bg="rgba(251, 146, 60, 0.1)" $color="#fb923c">
                            {ind}
                          </QualityTag>
                        ))}
                      </QualityTagRow>
                    )}
                  </ReasoningBox>
                )}

                {sq.publisher && (
                  <ReasoningBox $color="rgba(99, 102, 241, 0.5)">
                    <ReasoningLabel><Book /> Julkaisija</ReasoningLabel>
                    {sq.publisher}
                    {sq.authors && sq.authors.length > 0 && (
                      <div style={{ marginTop: 4, fontSize: 13, color: '#888' }}>
                        Tekijät: {sq.authors.join(', ')}
                      </div>
                    )}
                  </ReasoningBox>
                )}
              </Section>
            );
          })()}

          {document.topics && document.topics.length > 0 && (
            <Section>
              <SectionTitle>Teemat</SectionTitle>
              <Topics>
                {document.topics.map((topic, i) => (
                  <TopicTag key={i}>{topic}</TopicTag>
                ))}
              </Topics>
            </Section>
          )}

          <Section>
            <SectionTitle>Tiedot</SectionTitle>
            <MetadataGrid>
              <MetadataItem>
                <MetadataLabel>Sivuja</MetadataLabel>
                <MetadataValue>{document.total_pages || '-'}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Chunkkeja</MetadataLabel>
                <MetadataValue>{document.chunk_count || 0}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Sanoja</MetadataLabel>
                <MetadataValue>{document.word_count?.toLocaleString() || '-'}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Kieli</MetadataLabel>
                <MetadataValue>{document.language || 'fi'}</MetadataValue>
              </MetadataItem>
              <MetadataItem>
                <MetadataLabel>Indeksoitu</MetadataLabel>
                <MetadataValue>{formatDate(document.created_at, language)}</MetadataValue>
              </MetadataItem>
              {document.synopsis_generated_at && (
                <MetadataItem>
                  <MetadataLabel>Tiivistelmä luotu</MetadataLabel>
                  <MetadataValue>{formatDate(document.synopsis_generated_at, language)}</MetadataValue>
                </MetadataItem>
              )}
            </MetadataGrid>
          </Section>

          {/* AI Enrichment stats */}
          {enrichmentStats && (
            <ChunksSection>
              <ChunksHeader onClick={() => setShowEnrichment(!showEnrichment)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Microscope size={16} style={{ color: '#c084fc' }} />
                  AI-rikastus ({enrichmentStats.enrichedChunks}/{enrichmentStats.totalChunks} chunkkia — {enrichmentStats.enrichmentPct}%)
                </span>
                {showEnrichment ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </ChunksHeader>

              {showEnrichment && (
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {enrichmentStats.topTags?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>Avainsanat</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {enrichmentStats.topTags.map(tag => (
                          <span key={tag} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, background: 'rgba(147, 51, 234, 0.15)', color: '#c084fc' }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {enrichmentStats.sampleQuestions?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>Esimerkkikysymyksiä (haun parantamiseksi)</div>
                      {enrichmentStats.sampleQuestions.map((q, i) => (
                        <div key={i} style={{ fontSize: 13, color: '#aaa', padding: '3px 0', lineHeight: 1.4 }}>• {q}</div>
                      ))}
                    </div>
                  )}
                  {/* Per-chunk enrichment details */}
                  {chunks.filter(c => c.metadata?.enriched).slice(0, 5).map((chunk, i) => (
                    <div key={chunk.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Chunk {chunk.chunk_index + 1} — {chunk.section_heading || '—'}</div>
                      <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.5 }}>{chunk.metadata.summary}</div>
                      {chunk.metadata.tags?.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {chunk.metadata.tags.map(t => (
                            <span key={t} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: '#888' }}>{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ChunksSection>
          )}

          <ChunksSection>
            <ChunksHeader onClick={handleToggleChunks}>
              <span>Sisältö ({chunks.length > 0 ? `${chunks.length} chunkkia` : 'lataa'})</span>
              {showChunks ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ChunksHeader>

            {showChunks && (
              loadingChunks ? (
                <LoadingChunks>Ladataan...</LoadingChunks>
              ) : (
                <ChunksList>
                  {chunks.map((chunk, i) => (
                    <ChunkItem key={chunk.id || i}>
                      <strong>
                        {chunk.section_heading && `${chunk.section_heading} `}
                        {chunk.page_number && `(Sivu ${chunk.page_number})`}
                      </strong>
                      {chunk.content}
                      {chunk.metadata?.enriched && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(147, 51, 234, 0.08)', borderRadius: 6, fontSize: 12 }}>
                          <span style={{ color: '#c084fc', fontWeight: 600 }}>AI: </span>
                          <span style={{ color: '#999' }}>{chunk.metadata.summary}</span>
                        </div>
                      )}
                    </ChunkItem>
                  ))}
                </ChunksList>
              )
            )}
          </ChunksSection>
        </ModalCard.Body>
      </ModalCard>
    </ModalWrapper>
  );
};

export default KnowledgeDetailModal;
